import React, { useEffect, useMemo, useState } from "react";
import { Redirect, useHistory, useRouteMatch } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import axios from "axios";

import CustomerProfile from "./profile/CustomerProfile";
import CustomerReservations from "./reservations/CustomerReservations";
import CustomerNotes from "./notes/CustomerNotes";
import CustomerMemberships from "./memberships/CustomerMemberships";
import CustomerHouseAccounts from "./houseAccounts/CustomerHouseAccounts";
import CustomerPaymentMethods from "./paymentMethods/CustomerPaymentMethods";
import CustomerItemSales from "./itemSales/CustomerItemSales";
import CustomerCredit from "./credit/CustomerCredit";

import "./customerTabs.scss";
import { useCustomerContext } from "../context/CustomerContext";
import CustomerTickets from "./tickets/CustomerTickets";
import CustomerLoyaltyProgram from "./loyaltyProgram/CustomerLoyaltyProgram";
import { useAppSelector } from "hooks/redux";
import { IFacilityState } from "redux/reducers/facility";
import CustomerPlayingPartners from "./playingPartners/CustomerPlayingPartners";
import Tabs from "components/tabs/Tabs";
import { StatusCode } from "api/protocols";
import { GetCustomerNotes } from "api/rpc/2024-04/facilityAdmin/customer/customer";

type TabNavigationType = {
  title: string;
  route: string;
  noteIndicator?: boolean;
};

/**
 * Handles Tab navigation within Customer.tsx on facility-side.
 *     - Add a new tab using this element.
 *
 * @property {string | number} customerId used to generate and define active URL routes.
 * @returns {JSX.Element} A navigation list with the tab content displayed under it.
 */
export default function CustomerTabs(props: { customerId: string | number }): JSX.Element {
  const routeMatch = useRouteMatch();
  const history = useHistory();
  const { t } = useTranslation();
  const { facilityStore } = useAppSelector(store => store);
  const { state, updateState } = useCustomerContext();
  const [selectedTab, setSelectedTab] = useState(0);

  const customerTabInfo = useMemo(
    () => generateCustomerTabs(props.customerId, t, facilityStore),
    [props.customerId, t, facilityStore?.loyaltyProgram],
  );

  const tabs = useMemo(
    () =>
      customerTabInfo.map(tab => ({
        content: tab.title,
        counter: tab.noteIndicator && state?.notes?.length > 0 ? state.notes.length : undefined,
        route: tab.route,
      })),
    [customerTabInfo, state?.notes?.length],
  );

  useEffect(() => {
    const source = axios.CancelToken.source();

    if (state.notes === null && state.customer?.id) {
      void (async () => {
        const customerNotesRes = await GetCustomerNotes(
          { customer_id: state.customer.id },
          false,
          source.token,
        );

        if (source.token.reason || customerNotesRes.status !== StatusCode.OK) {
          return;
        }

        updateState({ notes: customerNotesRes.data });
      })();
    }

    return () => {
      source.cancel();
    };
  }, [state.customer?.id]);

  useEffect(() => {
    const tabIndex = tabs.findIndex(tab => tab.route === routeMatch.url);
    if (tabIndex !== -1) {
      setSelectedTab(tabIndex);
    }
  }, [routeMatch.url, tabs]);

  const handleTabChange = (index: number) => {
    setSelectedTab(index);
    history.push(tabs[index].route);
  };

  return (
    <>
      <div className="customer-tabs-underline">
        <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange} variant="underline" />
      </div>

      {displayCustomerPage(props.customerId, routeMatch.url)}
    </>
  );
}

/** Define the URL routes for the specific customer.  Returns array for profile navigation. */
const generateCustomerTabs = (
  customerId: number | string,
  t: TFunction<"translation", undefined>,
  facilityStore: IFacilityState,
): TabNavigationType[] => {
  if (!customerId) {
    return [];
  }

  const idAsString = typeof customerId === "number" ? customerId.toString() : customerId;

  return [
    {
      title: t("Profile"),
      route: `/admin/customers/${idAsString}/profile`,
    },
    {
      title: t("Bookings"),
      route: `/admin/customers/${idAsString}/reservations`,
    },
    {
      title: t("Memberships"),
      route: `/admin/customers/${idAsString}/membership`,
    },
    {
      title: t("Tickets"),
      route: `/admin/customers/${idAsString}/tickets`,
    },
    {
      title: t("House Accounts"),
      route: `/admin/customers/${idAsString}/house-accounts`,
    },
    {
      title: t("Payment Methods"),
      route: `/admin/customers/${idAsString}/payment-methods`,
    },
    {
      title: t("Item Sales"),
      route: `/admin/customers/${idAsString}/item-sales`,
    },
    {
      title: t("Credit Book"),
      route: `/admin/customers/${idAsString}/credit`,
    },
    ...(facilityStore?.loyaltyProgram
      ? [
          {
            title: t("Loyalty Program"),
            route: `/admin/customers/${idAsString}/loyalty-program`,
          },
        ]
      : []),
    {
      title: t("Notes"),
      route: `/admin/customers/${idAsString}/notes`,
      noteIndicator: true,
    },
    {
      title: t("Playing Partners"),
      route: `/admin/customers/${idAsString}/playing-partners`,
    },
  ];
};

/** Returns a JSX element based on the given customerID and a defined route URL. */
const displayCustomerPage = (customerId: number | string, routeURL: string): JSX.Element => {
  const idAsString = typeof customerId === "number" ? customerId.toString() : customerId;

  switch (routeURL) {
    case `/admin/customers/${idAsString}/profile`:
      return <CustomerProfile />;
    case `/admin/customers/${idAsString}/reservations`:
      return <CustomerReservations />;
    case `/admin/customers/${idAsString}/membership`:
      return <CustomerMemberships />;
    case `/admin/customers/${idAsString}/tickets`:
      return <CustomerTickets />;
    case `/admin/customers/${idAsString}/house-accounts`:
      return <CustomerHouseAccounts />;
    case `/admin/customers/${idAsString}/payment-methods`:
      return <CustomerPaymentMethods />;
    case `/admin/customers/${idAsString}/item-sales`:
      return <CustomerItemSales />;
    case `/admin/customers/${idAsString}/credit`:
      return <CustomerCredit customerId={idAsString} />;
    case `/admin/customers/${idAsString}/loyalty-program`:
      return <CustomerLoyaltyProgram customerId={idAsString} />;
    case `/admin/customers/${idAsString}/notes`:
      return <CustomerNotes />;
    case `/admin/customers/${idAsString}/playing-partners`:
      return <CustomerPlayingPartners />;

    default:
      return <Redirect to={`/admin/customers/${idAsString}/profile`} />;
  }
};
